import type { MigrationEvent } from "../../api/types";
import { formatDateTimeShort } from "../../api/types";
import { Disclosure } from "../../components";
import { t, tDynamic } from "../../i18n/t";

/**
 * §B6.1, redrawn as the §3 timeline rail (Status.html): time in Geist Mono, a node per event — the newest
 * (current) gets the accent halo, a `failed` transition the fail mark — on the 1 px connector, then the
 * transition and the actor. The events arrive from 06a's `useEvents` **newest first** — the hook does the
 * `?since_id=` bookkeeping, the accumulation and the reversal (§A4.5). This component sorts nothing.
 *
 * The old table's payload column (the sign-off hash expansion) is gone with the table: the sign-off dialog
 * is where hashes are signed and shown (§B5.4), and the design's timeline has no details column.
 */
export function Timeline({ events }: { events: readonly MigrationEvent[] }): JSX.Element {
  return (
    <Disclosure
      asHeading
      title={t("status.timeline")}
      counts={t("status.timeline_counts", { n: events.length })}
      testId="status-timeline-section"
    >
      {events.length === 0 ? (
        <p className="tline__empty">{t("status.empty")}</p>
      ) : (
        <ol className="tline" data-testid="timeline">
          {events.map((event, index) => {
            const nodeClass =
              event.to_state === "failed"
                ? "tline__node tline__node--failed"
                : index === 0
                  ? "tline__node tline__node--current"
                  : "tline__node";
            return (
              <li className="tline__event" key={event.id}>
                <span className="tline__time">{formatDateTimeShort(event.created_at)}</span>
                <span className="tline__rail" aria-hidden="true">
                  <span className={nodeClass} />
                  <span className="tline__line" />
                </span>
                <span className="tline__body">
                  <span className="tline__title">
                    {t("status.transition", {
                      from:
                        event.from_state === null ? t("common.none") : tDynamic("state.", event.from_state),
                      to: tDynamic("state.", event.to_state),
                    })}
                  </span>
                  <span className="tline__sub">
                    {event.actor}
                    {event.note === null || event.note === "" ? "" : ` · ${event.note}`}
                  </span>
                </span>
              </li>
            );
          })}
        </ol>
      )}
    </Disclosure>
  );
}
